import React from 'react';
import { Link } from 'react-router-dom';
import {
  Bot, Zap, Globe, MessageSquare, CreditCard, Calendar,
  FileText, Workflow, ArrowRight, CheckCircle2, Sparkles, Phone, TrendingUp, Minus,
} from 'lucide-react';
import './AiAutopilotBasePackagePage.css';

const PLATINUM_CHECKOUT = 'https://www.fanbasis.com/agency-checkout/Formalizeai/4W24x';
const GOLD_CHECKOUT = 'https://commas.com/checkout/2qM2v6HBCm8hU8J';

const GOLD_INCLUDES = [
  'Custom AI Website (niche-specific copy & design)',
  'Mobile-Optimized & SEO-Ready',
  'AI Chatbot (answers questions & captures leads)',
  'Lead Capture Forms + Email Notifications',
  'Scheduling & Booking System',
  'Basic Analytics Dashboard',
];

const GOLD_UPGRADE_NUDGES = [
  '24/7 AI Sales Closer Bot',
  'AI Customer Support Agent',
  'Full CRM & Pipeline Automation',
  'Contracts, Invoicing & Onboarding',
];

const COMPARE_ROWS = [
  { label: 'Custom AI Website (niche-specific)', gold: true, platinum: true },
  { label: 'Mobile-Optimized & SEO-Ready', gold: true, platinum: true },
  { label: 'AI Chatbot (Q&A + Lead Capture)', gold: true, platinum: true },
  { label: 'Lead Capture Forms + Email Notifications', gold: true, platinum: true },
  { label: 'Scheduling & Booking System', gold: true, platinum: true },
  { label: 'Basic Analytics Dashboard', gold: true, platinum: true },
  { label: '24/7 AI Sales Closer Bot', gold: false, platinum: true },
  { label: 'AI Customer Support Agent', gold: false, platinum: true },
  { label: 'CRM + Pipeline Integration', gold: false, platinum: true },
  { label: 'Payment Processing Setup', gold: false, platinum: true },
  { label: 'Contracts & E-Sign Automation', gold: false, platinum: true },
  { label: 'Client Onboarding Sequences', gold: false, platinum: true },
  { label: 'Invoicing System', gold: false, platinum: true },
  { label: 'Full End-to-End Workflow Automation', gold: false, platinum: true },
];

const PLATINUM_INCLUDES = [
  'Custom AI Website (niche-specific copy & design)',
  'AI Sales Agent (24/7 Closer Bot)',
  'AI Customer Support Agent',
  'CRM + Pipeline Integration',
  'Payment Processing Setup',
  'Scheduling & Booking System',
  'Contracts & E-Sign Automation',
  'Client Onboarding Sequences',
  'Invoicing System',
  'Full End-to-End Workflow Automation',
];

const FEATURES = [
  {
    icon: Globe,
    title: 'Custom AI Website (Niche-Specific)',
    desc: 'Professionally written, on-brand sales site built specifically for your niche — copywriting, funnels, and design done for you.',
  },
  {
    icon: Bot,
    title: '24/7 AI Sales Agent',
    desc: 'Closer-bot trained on YOUR services, pricing, and objections. Qualifies leads, books calls, takes payments, and upsells while you sleep.',
  },
  {
    icon: MessageSquare,
    title: 'AI Customer Support Agent',
    desc: 'Handles FAQs, order status, service questions, and refund policies — reducing human support to near-zero.',
  },
  {
    icon: Workflow,
    title: 'CRM + Pipeline Integration',
    desc: 'Every lead, conversation, and sale flows into a clean pipeline automatically. No manual data entry, ever.',
  },
  {
    icon: CreditCard,
    title: 'Payment Processing Setup',
    desc: 'Stripe, Square, or Fanbasis wired into your funnel so clients can pay you directly from the chat or checkout.',
  },
  {
    icon: Calendar,
    title: 'Scheduling & Booking System',
    desc: 'Auto-booked discovery calls land on your calendar after the AI qualifies the lead. Wake up to a full schedule.',
  },
  {
    icon: FileText,
    title: 'Contracts & Onboarding Automation',
    desc: 'E-signed contracts, welcome emails, and onboarding sequences trigger the second a client pays.',
  },
  {
    icon: Zap,
    title: 'Full Business Workflow Automation',
    desc: 'End-to-end system: lead capture → qualification → close → onboard → deliver → invoice → review request. All hands-free.',
  },
];

const NICHES = [
  'Coaches & Consultants',
  'Real Estate Pros',
  'Service Businesses',
  'Local Businesses',
  'Online Brands',
  'Course Creators',
  'Agency Owners',
  'E-commerce Stores',
];

const AiAutopilotBasePackagePage = () => {
  return (
    <div className="dark-page autopilot-page">
      {/* Hero */}
      <section className="autopilot-hero">
        <div className="autopilot-hero-inner">
          <div className="autopilot-badge" data-testid="autopilot-badge">
            <Sparkles size={14} /> AI BUSINESS AUTOPILOT™ · GOLD & PLATINUM
          </div>
          <h1 className="display-huge">
            Stop Working <span className="text-gradient">IN</span> Your Business.<br />
            Let AI Work <span className="text-gradient">FOR</span> You.
          </h1>
          <p className="body-large autopilot-subtitle">
            We build you a fully functional, AI-powered business system that runs your operations,
            sales, support, and marketing — with minimal to no daily work from you. This isn't just
            a website. It's a fully autonomous digital employee that sells, books, supports, markets,
            and collects money 24/7.
          </p>

          <div className="autopilot-hero-cta">
            <a
              href="#packages"
              className="btn-primary autopilot-cta-btn"
              data-testid="autopilot-cta-top"
            >
              See The Packages <ArrowRight size={20} />
            </a>
            <a
              href="https://calendly.com/thecollinsacademynew/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="autopilot-call-link"
              data-testid="autopilot-call-link"
            >
              <Phone size={14} /> Schedule a strategy call first
            </a>
          </div>

          <div className="autopilot-hero-stats">
            <div className="autopilot-hero-stat">
              <div className="autopilot-hero-stat-num">24/7</div>
              <div className="autopilot-hero-stat-label">AI Selling & Supporting</div>
            </div>
            <div className="autopilot-hero-stat">
              <div className="autopilot-hero-stat-num">0 hrs</div>
              <div className="autopilot-hero-stat-label">Daily Manual Work</div>
            </div>
            <div className="autopilot-hero-stat">
              <div className="autopilot-hero-stat-num">ANY</div>
              <div className="autopilot-hero-stat-label">Niche Supported</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="autopilot-features">
        <h2 className="display-medium autopilot-section-title">Everything Built For You</h2>
        <p className="autopilot-section-sub">
          You don't get a template. You get a complete, autonomous digital employee.
        </p>
        <div className="autopilot-features-grid">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="autopilot-feature-card">
                <div className="autopilot-feature-icon"><Icon size={22} /></div>
                <h3 className="autopilot-feature-title">{f.title}</h3>
                <p className="autopilot-feature-desc">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Niches */}
      <section className="autopilot-niches">
        <h2 className="display-medium autopilot-section-title">Built For Any Niche</h2>
        <p className="autopilot-section-sub">
          We tailor the AI website, the sales agent, and the support agent to your exact industry.
        </p>
        <div className="autopilot-niches-grid">
          {NICHES.map((n) => (
            <div key={n} className="autopilot-niche-pill">
              <CheckCircle2 size={14} /> {n}
            </div>
          ))}
        </div>
      </section>

      {/* Packages: Gold + Platinum */}
      <section id="packages" className="autopilot-includes">
        <h2 className="display-medium autopilot-section-title">Choose Your Package</h2>
        <p className="autopilot-section-sub">
          Start lean with Gold, or go all-in with Platinum for the full autonomous business system.
        </p>
        <div className="autopilot-packages-grid">
          {/* Gold */}
          <div className="autopilot-package-card autopilot-package-gold" data-testid="autopilot-package-gold">
            <div className="autopilot-package-tier">GOLD</div>
            <h3 className="autopilot-package-name">The Starter Kit</h3>
            <p className="autopilot-package-tag">
              Perfect entry point. Get a professional AI-powered presence without the full automation stack.
            </p>
            <ul className="autopilot-includes-list autopilot-package-list">
              {GOLD_INCLUDES.map((line, i) => (
                <li key={i}><CheckCircle2 size={16} /> {line}</li>
              ))}
            </ul>

            <div className="autopilot-upgrade-nudge" data-testid="autopilot-upgrade-nudge">
              <div className="autopilot-upgrade-nudge-head">
                <TrendingUp size={14} />
                <span>Ready for the full stack? Upgrade to Platinum for:</span>
              </div>
              <ul className="autopilot-upgrade-nudge-list">
                {GOLD_UPGRADE_NUDGES.map((line, i) => (
                  <li key={i}>+ {line}</li>
                ))}
              </ul>
              <a href="#packages" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="autopilot-cta-platinum"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }} className="autopilot-upgrade-nudge-link" data-testid="autopilot-upgrade-nudge-link">
                See Platinum <ArrowRight size={12} />
              </a>
            </div>

            <div className="autopilot-package-cta">
              <a
                href={GOLD_CHECKOUT}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary autopilot-cta-btn autopilot-cta-btn-lg"
                data-testid="autopilot-cta-gold"
              >
                Get Gold Package <ArrowRight size={18} />
              </a>
              <p className="autopilot-foot-note">
                Secure checkout. Includes AI Website, Chatbot, and Booking setup.
              </p>
            </div>
          </div>

          {/* Platinum */}
          <div className="autopilot-package-card autopilot-package-platinum" data-testid="autopilot-package-platinum">
            <div className="autopilot-package-ribbon">Most Popular</div>
            <div className="autopilot-package-tier">PLATINUM</div>
            <h3 className="autopilot-package-name">The Full Autopilot</h3>
            <p className="autopilot-package-tag">
              Complete, autonomous digital employee. Sales, support, onboarding, and workflows — 100% hands-free.
            </p>
            <ul className="autopilot-includes-list autopilot-package-list">
              {PLATINUM_INCLUDES.map((line, i) => (
                <li key={i}><CheckCircle2 size={16} /> {line}</li>
              ))}
            </ul>
            <div className="autopilot-package-cta">
              <a
                href={PLATINUM_CHECKOUT}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary autopilot-cta-btn autopilot-cta-btn-lg"
                data-testid="autopilot-cta-platinum"
              >
                Get Platinum Package <ArrowRight size={18} />
              </a>
              <p className="autopilot-foot-note">
                Secure checkout via The Wealth Collection. Full setup, integrations, and AI training included.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Compare Gold vs Platinum */}
      <section className="autopilot-compare" data-testid="autopilot-compare-section">
        <h2 className="display-medium autopilot-section-title">Gold vs Platinum — Side by Side</h2>
        <p className="autopilot-section-sub">
          Every feature, laid out clearly. Still on the fence? This is how the two tiers stack up.
        </p>

        <div className="autopilot-compare-wrap">
          <div className="autopilot-compare-table" role="table" aria-label="Gold vs Platinum feature comparison">
            <div className="autopilot-compare-head" role="row">
              <div className="autopilot-compare-cell autopilot-compare-cell-feature" role="columnheader">Feature</div>
              <div className="autopilot-compare-cell autopilot-compare-cell-tier" role="columnheader">
                <span className="autopilot-compare-tier-label autopilot-compare-tier-gold">Gold</span>
              </div>
              <div className="autopilot-compare-cell autopilot-compare-cell-tier autopilot-compare-cell-platinum" role="columnheader">
                <span className="autopilot-compare-tier-label autopilot-compare-tier-platinum">Platinum</span>
                <span className="autopilot-compare-ribbon-sm">Most Popular</span>
              </div>
            </div>

            {COMPARE_ROWS.map((row, i) => (
              <div className="autopilot-compare-row" role="row" key={i} data-testid={`compare-row-${i}`}>
                <div className="autopilot-compare-cell autopilot-compare-cell-feature" role="cell">{row.label}</div>
                <div className="autopilot-compare-cell autopilot-compare-cell-tier" role="cell">
                  {row.gold ? (
                    <CheckCircle2 size={20} className="autopilot-compare-check" />
                  ) : (
                    <Minus size={18} className="autopilot-compare-dash" />
                  )}
                </div>
                <div className="autopilot-compare-cell autopilot-compare-cell-tier autopilot-compare-cell-platinum" role="cell">
                  {row.platinum ? (
                    <CheckCircle2 size={20} className="autopilot-compare-check autopilot-compare-check-platinum" />
                  ) : (
                    <Minus size={18} className="autopilot-compare-dash" />
                  )}
                </div>
              </div>
            ))}

            <div className="autopilot-compare-foot" role="row">
              <div className="autopilot-compare-cell autopilot-compare-cell-feature" role="cell" />
              <div className="autopilot-compare-cell autopilot-compare-cell-tier" role="cell">
                <a
                  href={GOLD_CHECKOUT}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary autopilot-compare-cta"
                  data-testid="compare-cta-gold"
                >
                  Choose Gold
                </a>
              </div>
              <div className="autopilot-compare-cell autopilot-compare-cell-tier autopilot-compare-cell-platinum" role="cell">
                <a
                  href={PLATINUM_CHECKOUT}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary autopilot-compare-cta"
                  data-testid="compare-cta-platinum"
                >
                  Choose Platinum
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-sell */}
      <section className="autopilot-cross">
        <div className="autopilot-cross-card">
          <h3>Need to build your business credit profile first?</h3>
          <p>Run a free 60-second credit scan to see exactly what you qualify for — then plug it into your new AI business.</p>
          <Link to="/funding-qualifier" className="btn-secondary" data-testid="autopilot-cross-cta">
            Run My Free Credit Scan <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AiAutopilotBasePackagePage;
